/**
 * Email templates: the HTML the network's emails are wrapped in, and the
 * inline styles the API puts on headings, paragraphs and links. A `DEFAULT`
 * template applies to every email; a `SPECIFIC` one only to the email
 * translations that pick it. An empty template uses the API's own structure.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { getContext } from '@/services/networks/networksApi'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** `EmailTemplate.Type` in core. */
export const EMAIL_TEMPLATE_TYPES = ['DEFAULT', 'SPECIFIC'] as const
export type EmailTemplateType = (typeof EMAIL_TEMPLATE_TYPES)[number]

/** The inline style fields, in the order the edit page shows them. */
export const EMAIL_TEMPLATE_STYLE_FIELDS = [
  'h1',
  'h2',
  'pLarge',
  'pMedium',
  'pSmall',
  'pSmaller',
  'a',
] as const
export type EmailTemplateStyleField = (typeof EMAIL_TEMPLATE_STYLE_FIELDS)[number]

/** The styles the API applies when a field is blank — the constants on `EmailTemplate` in core. */
export const EMAIL_TEMPLATE_STYLE_DEFAULTS: Record<EmailTemplateStyleField, string> = {
  h1: 'font-size: 25px;padding: 10px 0;margin: 0;line-height: 1.15em;',
  h2: 'font-size: 20px;padding: 10px 0;margin: 0;line-height: 1.15em;',
  pLarge: 'font-size: 15px;padding: 8px 0;margin: 0;line-height: 1.15em;',
  pMedium: 'font-size: 13px;padding: 7px 0;margin: 0;line-height: 1.15em;',
  pSmall: 'font-size: 10px;padding: 4px 0;margin: 0;line-height: 1.15em;',
  pSmaller: 'font-size: 5px;padding: 3px 0;margin: 0;line-height: 1.15em;',
  a: '',
}

export interface EmailTemplateRef {
  id: string
  name: string
}

export interface EmailTemplate extends Record<EmailTemplateStyleField, string> {
  id: string
  name: string
  description: string
  type: EmailTemplateType
  /** Empty uses the API's default structure. */
  template: string
  team: EmailTemplateRef | null
  objectState: string
  createTime: string
  updateTime: string
  validationWarnings: ValidationWarning[]
}

export interface EmailTemplateInput extends Partial<Record<EmailTemplateStyleField, string>> {
  name: string
  description?: string
  type?: EmailTemplateType
  template?: string
  /** `null` takes the template off its team. */
  team?: { id: string } | null
  objectState?: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

/** A reference the API sends as an id or as an `{ id, name }` object. */
export function mapRef(value: unknown): EmailTemplateRef | null {
  if (typeof value === 'string') return value ? { id: value, name: '' } : null
  const ref = value as Record<string, unknown> | null | undefined
  return ref?.id ? { id: str(ref.id), name: str(ref.name) } : null
}

function mapEmailTemplate(r: Record<string, unknown>): EmailTemplate {
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    type: (r.type as EmailTemplateType) ?? 'SPECIFIC',
    template: str(r.template),
    h1: str(r.h1),
    h2: str(r.h2),
    pLarge: str(r.pLarge),
    pMedium: str(r.pMedium),
    pSmall: str(r.pSmall),
    pSmaller: str(r.pSmaller),
    a: str(r.a),
    team: mapRef(r.team),
    objectState: str(r.objectState),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface EmailTemplateListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of email templates, narrowed by the given filters on the server. */
async function fetchEmailTemplatePage(
  { search, status, sort }: EmailTemplateListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<EmailTemplate>> {
  const params = new URLSearchParams({
    ...searchParams('emailTemplates', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
    // On a marketplace, limits a team admin to their own teams' templates.
    showEditableOnly: 'true',
  })
  const res = await apiFetch(`${API_BASE}/emailtemplates?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch email templates')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapEmailTemplate)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const EmailTemplatesApi = {
  getPage: ({ page = 1, pageSize = 25, ...filters }: EmailTemplateListOptions = {}) =>
    fetchEmailTemplatePage(filters, page, pageSize),

  /** Every email template matching the search, walked page by page — for pickers. */
  getAll: (search?: string): Promise<EmailTemplate[]> =>
    fetchAllPages((page, pageSize) => fetchEmailTemplatePage({ search }, page, pageSize)),

  getById: async (id: string): Promise<EmailTemplate> => {
    const res = await apiFetch(`${API_BASE}/emailtemplates/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch email template')
    return mapEmailTemplate(await res.json())
  },

  create: async (id: string, input: EmailTemplateInput): Promise<EmailTemplate> => {
    const res = await apiFetch(`${API_BASE}/emailtemplates/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create email template')
    return mapEmailTemplate(await res.json())
  },

  update: async (id: string, input: EmailTemplateInput): Promise<EmailTemplate> => {
    const res = await apiFetch(`${API_BASE}/emailtemplates/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save email template')
    return mapEmailTemplate(await res.json())
  },
}
